const HAN_RE = /[\u3400-\u9fff]/;
const LATIN_RE = /[A-Za-z]/;
const BILINGUAL_SEPARATOR_RE = /\s+\/\s+/;

type LocalizedMessage = {
  zh: string;
  en: string;
};

const GENERIC_ERROR: LocalizedMessage = {
  zh: '操作失败，请检查输入后重试。',
  en: 'Operation failed. Check your input and try again.',
};

const STANDARD_MESSAGES: Record<string, LocalizedMessage> = {
  'Internal server error': {
    zh: '服务器处理失败，请稍后重试。',
    en: 'The server could not complete the request. Try again later.',
  },
  Unauthorized: {
    zh: '登录状态已失效，请重新登录。',
    en: 'Your session has expired. Sign in again.',
  },
  'Forbidden resource': {
    zh: '您没有执行此操作的权限。',
    en: 'You do not have permission to perform this action.',
  },
  Forbidden: {
    zh: '您没有执行此操作的权限。',
    en: 'You do not have permission to perform this action.',
  },
  'Not Found': {
    zh: '请求的内容不存在或已被删除。',
    en: 'The requested item does not exist or has been deleted.',
  },
  'Bad Request': {
    zh: '请求内容有误，请检查后重试。',
    en: 'The request is invalid. Check your input and try again.',
  },
  'Validation failed': {
    zh: '输入内容校验失败，请检查后重试。',
    en: 'Validation failed. Check your input and try again.',
  },
};

function currentLanguage(language?: string): 'zh' | 'en' {
  return language?.toLowerCase().startsWith('en') ? 'en' : 'zh';
}

function appendRightSideDetail(left: string, right: string): string {
  if (/[:：]/.test(left)) return left;
  const detail = right.match(/:\s*(.+)$/)?.[1]?.trim();
  if (!detail) return left;

  const localizedDetail = detail.replace(/\s*\(existing id ([^)]+)\)\s*$/i, '（已有记录编号 $1）');
  return `${left}：${localizedDetail}`;
}

function selectBilingualText(message: string, language: 'zh' | 'en'): string | null {
  const parts = message.split(BILINGUAL_SEPARATOR_RE);
  if (parts.length < 2) return null;

  const splitAt = parts.findIndex(
    (_part, index) =>
      index < parts.length - 1 &&
      HAN_RE.test(parts.slice(0, index + 1).join(' / ')) &&
      LATIN_RE.test(parts.slice(index + 1).join(' / ')),
  );
  if (splitAt < 0) return null;

  const zh = parts
    .slice(0, splitAt + 1)
    .join(' / ')
    .trim();
  const en = parts
    .slice(splitAt + 1)
    .join(' / ')
    .trim();
  return language === 'en' ? en : appendRightSideDetail(zh, en);
}

function localizeRequiredField(message: string, language: 'zh' | 'en'): string | null {
  const match = message.match(
    /^(?:Required field cannot be empty|必填字段不能为空)\s*[:：]\s*(.+)$/i,
  );
  if (!match) return null;

  const rawField = match[1]?.trim();
  if (!rawField) return null;
  const field = selectBilingualText(rawField, language) ?? rawField;
  return language === 'en'
    ? `Required field cannot be empty: ${field}`
    : `必填字段不能为空：${field}`;
}

function localizeDuplicateAccount(message: string, language: 'zh' | 'en'): string | null {
  const match = message.match(/^客户已存在[:：]\s*(.*?)\s*[，,]\s*所有人[:：]\s*(.*?)\s*[。.]?$/);
  const accountName = match?.[1]?.trim();
  const ownerName = match?.[2]?.trim();
  if (!accountName || !ownerName) return null;

  return language === 'en'
    ? `Account already exists: ${accountName}. Owner: ${ownerName}.`
    : `客户已存在：${accountName}，所有人：${ownerName}。`;
}

function looksLikeTechnicalError(message: string): boolean {
  return (
    /\b(?:Prisma|TypeError|ReferenceError|SyntaxError|QueryFailedError|ECONN|ETIMEDOUT)\b/i.test(
      message,
    ) ||
    /\bat\s+\S+\s+\(.+:\d+:\d+\)/.test(message) ||
    /Invalid `prisma\./i.test(message) ||
    /Expected ISO-8601|Unknown argument|Foreign key constraint|Unique constraint/i.test(message)
  );
}

/**
 * Converts API error text to the active UI language before page components display it.
 * Existing bilingual backend messages use the stable "中文 / English" convention.
 */
export function localizeApiErrorMessage(message: unknown, language?: string): string {
  const lang = currentLanguage(language);
  if (Array.isArray(message)) {
    const localized = message.map((item) => localizeApiErrorMessage(item, lang)).filter(Boolean);
    return [...new Set(localized)].join(lang === 'en' ? '; ' : '；');
  }
  if (typeof message !== 'string' || !message.trim()) return GENERIC_ERROR[lang];

  const value = message.trim();
  const standard = STANDARD_MESSAGES[value];
  if (standard) return standard[lang];

  const required = localizeRequiredField(value, lang);
  if (required) return required;

  const duplicateAccount = localizeDuplicateAccount(value, lang);
  if (duplicateAccount) return duplicateAccount;

  const bilingual = selectBilingualText(value, lang);
  if (bilingual) return bilingual;

  if (looksLikeTechnicalError(value)) return GENERIC_ERROR[lang];

  const hasHan = HAN_RE.test(value);
  if ((lang === 'en' && hasHan) || (lang === 'zh' && !hasHan && LATIN_RE.test(value))) {
    return GENERIC_ERROR[lang];
  }
  return value;
}

export function normalizeApiErrorPayload(data: unknown, language?: string): unknown {
  if (typeof data === 'string') return localizeApiErrorMessage(data, language);
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;

  const payload = data as Record<string, unknown>;
  if (!('message' in payload)) return data;
  return {
    ...payload,
    message: localizeApiErrorMessage(payload.message, language),
  };
}
